'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    LinkableAppointmentType,
    linkBillingToAppointment,
} from '@/lib/api/financial/financial-billing-api'
import {
    NumberSearchHit,
    searchByNumber,
} from '@/lib/api/global-search/global-search-api'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { ReactNode, useState } from 'react'
import {
    MdBiotech,
    MdClose,
    MdEventNote,
    MdMedicalServices,
    MdSearch,
} from 'react-icons/md'
import { toast } from 'react-toastify'

interface Props {
    billingId: number
    /** Valor da cobrança, em centavos — usado para explicar o encaixe. */
    billingAmount: number
    /**
     * Atendimento a que a cobrança JÁ está atrelada. Presente, o modal deixa de ser
     * "vincular" e passa a ser "trocar": o vínculo atual sai (e o repasse dele é
     * cancelado) quando o novo entra. É por isso que ele aparece no topo, nomeado — a
     * troca só é segura se o operador enxerga de onde a cobrança está saindo.
     */
    currentLink?: { type: string; typeLabel: string; id: number; title?: string | null } | null
    onClose: () => void
    onLinked: () => void
}

const TYPE_ICON: Record<LinkableAppointmentType, ReactNode> = {
    EXAM: <MdBiotech className="text-info text-xl" />,
    PROCEDURE: <MdMedicalServices className="text-warning text-xl" />,
    CONSULTATION: <MdEventNote className="text-secondary text-xl" />,
}

const TYPE_BADGE: Record<LinkableAppointmentType, string> = {
    EXAM: 'badge-info',
    PROCEDURE: 'badge-warning',
    CONSULTATION: 'badge-secondary',
}

const formatMoney = (cents: number) =>
    (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })

/** Só atendimentos entram: cobrança não se atrela a cobrança. */
const isAppointment = (h: NumberSearchHit): boolean => h.type !== 'BILLING'

/**
 * Vincula uma cobrança avulsa a um atendimento, buscando pelo NÚMERO — o mesmo
 * hábito já usado na empresa (busca por número do documento). Diferente daquele
 * modal, aqui as cobranças são filtradas: só exame, consulta e procedimento.
 *
 * Quem decide como a cobrança é acomodada (âncora, substituição ou complemento)
 * é o backend — a tela só informa o resultado.
 */
export default function LinkAppointmentModal({
    billingId,
    billingAmount,
    currentLink,
    onClose,
    onLinked,
}: Props) {
    const [term, setTerm] = useState('')
    const [loading, setLoading] = useState(false)
    const [searched, setSearched] = useState(false)
    const [hits, setHits] = useState<NumberSearchHit[]>([])
    const [linkingId, setLinkingId] = useState<number | null>(null)
    // Troca pede confirmação: o vínculo anterior é desfeito e o repasse dele cancelado.
    const [confirmTarget, setConfirmTarget] = useState<NumberSearchHit | null>(null)

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault()
        const parsed = parseInt(term.trim(), 10)
        if (!Number.isFinite(parsed) || parsed <= 0) return

        setLoading(true)
        setSearched(false)
        try {
            // Escopo da organização: o pagamento cai numa filial e o atendimento pode
            // estar em outra.
            const res = await searchByNumber(parsed, 'organization')
            const found = (
                res.success && res.result ? res.result.hits : []
            ).filter(isAppointment)
            setHits(found)
            setSearched(true)
        } catch {
            setHits([])
            setSearched(true)
        } finally {
            setLoading(false)
        }
    }

    const handleLink = async (hit: NumberSearchHit) => {
        setConfirmTarget(null)
        setLinkingId(hit.id)
        try {
            const res = await linkBillingToAppointment(billingId, {
                appointmentType: hit.type as LinkableAppointmentType,
                appointmentId: hit.id,
            })
            if (res.success && res.result) {
                toast.success(res.result.message)
                onLinked()
                onClose()
            } else {
                toast.error(res.error || 'Não foi possível vincular a cobrança.')
            }
        } catch {
            toast.error('Não foi possível vincular a cobrança.')
        } finally {
            setLinkingId(null)
        }
    }

    return (
        <div className="modal modal-open" style={{ zIndex: 9999 }}>
            <div className="modal-box max-w-2xl">
                <div className="flex items-center justify-between mb-1">
                    <h3 className="font-bold text-lg">
                        {currentLink ? 'Trocar atendimento' : 'Vincular'}
                    </h3>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-circle"
                        onClick={onClose}
                        aria-label="Fechar"
                    >
                        <MdClose />
                    </button>
                </div>
                <p className="text-sm text-base-content/60 mb-1">
                    Informe o número do exame, consulta ou procedimento que este
                    pagamento de{' '}
                    <span className="font-semibold text-base-content">
                        {formatMoney(billingAmount)}
                    </span>{' '}
                    quitou.
                </p>
                {/* Só no vínculo novo: na troca, quem explica o efeito é o aviso
                    abaixo, e este texto ("nada é cancelado") contradiz o repasse que
                    a troca cancela. */}
                {!currentLink && (
                    <p className="text-xs text-base-content/50 mb-5">
                        A cobrança é apenas vinculada: se o atendimento ainda não tem
                        cobrança, esta vira a principal; se já tem, esta é empilhada
                        como pagamento adicional. Nenhuma outra cobrança é cancelada
                        ou alterada — se alguma precisar sair, cancele pelo
                        atendimento.
                    </p>
                )}

                {currentLink && (
                    <div className="mb-5 p-3 rounded-lg bg-warning/10 border border-warning/30">
                        <p className="text-sm">
                            Hoje esta cobrança está atrelada a{' '}
                            <span className="font-semibold">
                                {currentLink.typeLabel} #{currentLink.id}
                            </span>
                            {currentLink.title ? ` — ${currentLink.title}` : ''}.
                        </p>
                        <p className="text-xs text-base-content/60 mt-1">
                            Ao escolher outro atendimento, este vínculo é desfeito e o
                            repasse pendente gerado por ele é cancelado. Repasse já pago,
                            ou em lote de aprovação, impede a troca.
                        </p>
                    </div>
                )}

                <form onSubmit={handleSearch} className="form-control w-full">
                    <label className="label pb-1" htmlFor="link-appointment-number">
                        <span className="label-text font-medium">
                            Número do atendimento
                        </span>
                    </label>
                    <div className="flex gap-2">
                        <input
                            id="link-appointment-number"
                            type="number"
                            min={1}
                            autoFocus
                            className="input input-bordered flex-1"
                            placeholder="Ex.: 70921"
                            value={term}
                            onChange={e => setTerm(e.target.value)}
                        />
                        <button
                            type="submit"
                            className="btn btn-secondary"
                            disabled={loading || !term.trim()}
                        >
                            {loading ? (
                                <span className="loading loading-spinner loading-sm" />
                            ) : (
                                <MdSearch className="text-lg" />
                            )}
                            Buscar
                        </button>
                    </div>
                </form>

                {searched && hits.length === 0 && !loading && (
                    <div className="mt-6 p-4 rounded-lg bg-base-200/60 text-sm text-center text-base-content/70">
                        Nenhum exame, consulta ou procedimento com esse número.
                    </div>
                )}

                {hits.length > 0 && (
                    <div className="mt-6 flex flex-col gap-2 max-h-[50vh] overflow-y-auto">
                        <p className="text-xs text-base-content/50">
                            {currentLink
                                ? 'Escolha o atendimento para onde esta cobrança deve ir.'
                                : 'Escolha o atendimento que este pagamento quitou.'}
                        </p>
                        {hits.map(hit => {
                            const type = hit.type as LinkableAppointmentType
                            const sameAmount =
                                hit.amount != null && hit.amount === billingAmount
                            return (
                                <button
                                    key={`${hit.type}-${hit.id}`}
                                    type="button"
                                    disabled={linkingId !== null}
                                    onClick={() =>
                                        currentLink
                                            ? setConfirmTarget(hit)
                                            : handleLink(hit)
                                    }
                                    className="w-full text-left p-3 rounded-lg border border-base-300 hover:border-secondary hover:bg-base-200/60 transition-colors disabled:opacity-60"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="mt-0.5">
                                            {TYPE_ICON[type]}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span
                                                    className={`badge badge-sm ${TYPE_BADGE[type]}`}
                                                >
                                                    {hit.typeLabel}
                                                </span>
                                                <span className="font-medium truncate">
                                                    {hit.title}
                                                </span>
                                                {hit.statusLabel && (
                                                    <span className="badge badge-sm badge-ghost">
                                                        {hit.statusLabel}
                                                    </span>
                                                )}
                                                {sameAmount && (
                                                    <span className="badge badge-sm badge-success">
                                                        Mesmo valor
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-1 text-sm text-base-content/70 flex flex-wrap gap-x-4 gap-y-0.5">
                                                {hit.clientName && (
                                                    <span>
                                                        Paciente: {hit.clientName}
                                                    </span>
                                                )}
                                                {hit.professionalName && (
                                                    <span>
                                                        Profissional:{' '}
                                                        {hit.professionalName}
                                                    </span>
                                                )}
                                                {hit.date && (
                                                    <span>
                                                        {format(
                                                            new Date(hit.date),
                                                            "dd 'de' MMM 'de' yyyy",
                                                            { locale: ptBR }
                                                        )}
                                                    </span>
                                                )}
                                                {hit.amount != null &&
                                                    hit.amount > 0 && (
                                                        <span className="font-medium">
                                                            {formatMoney(hit.amount)}
                                                        </span>
                                                    )}
                                            </div>
                                        </div>
                                        {linkingId === hit.id && (
                                            <span className="loading loading-spinner loading-sm mt-1" />
                                        )}
                                    </div>
                                </button>
                            )
                        })}
                    </div>
                )}
            </div>
            <div className="modal-backdrop" onClick={onClose} />

            <ConfirmModal
                isOpen={!!confirmTarget}
                onClose={() => setConfirmTarget(null)}
                onConfirm={() => confirmTarget && handleLink(confirmTarget)}
                loading={linkingId !== null}
                title="Trocar o atendimento da cobrança"
                message={
                    currentLink && confirmTarget
                        ? `A cobrança sai de ${currentLink.typeLabel} #${currentLink.id} e passa para `
                          + `${confirmTarget.typeLabel} #${confirmTarget.id}. O repasse pendente do `
                          + 'atendimento atual será cancelado.'
                        : ''
                }
                confirmButtonText="Trocar"
                confirmButtonClass="btn-warning"
            />
        </div>
    )
}
